import {useEffect, useMemo, useState} from 'react';
import {Edge, Node} from 'reactflow';
import type {TrailMark} from '../utils/testRunState';
import type {WatchedTestRun} from './useTestRunWatch';

/** No test-run outlines: the strip is closed, or the canvas changed since the test started. */
const NO_TRAIL: ReadonlyMap<string, TrailMark> = new Map();

// Outlines belong to the canvas the test ran: editing a step or connection drops them, moving a card does not.
// Node data keeps its identity through drags and selection, so comparing it is enough.
export const useTestRunTrail = (
    testRun: WatchedTestRun | null,
    nodes: Node[],
    edges: Edge[]
): ReadonlyMap<string, TrailMark> => {
    const canvasShape = useMemo(() => ({
        steps: nodes.map(node => node.data),
        connections: edges.map(edge => `${edge.source}:${edge.sourceHandle ?? ''}>${edge.target}`).join('|'),
    }), [nodes, edges]);
    const [testedShape, setTestedShape] = useState<typeof canvasShape | null>(null);

    const testRunRootId = testRun?.rootId;
    useEffect(() => {
        // Taken when a test starts: what the outlines are drawn on.
        setTestedShape(testRunRootId ? canvasShape : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testRunRootId]);

    const trailValid = !!testedShape
        && testedShape.connections === canvasShape.connections
        && testedShape.steps.length === canvasShape.steps.length
        && testedShape.steps.every((data, index) => data === canvasShape.steps[index]);

    return testRun && trailValid ? testRun.state.trail : NO_TRAIL;
};
